import type {
  AnnualRevenue,
  ProductDescription,
} from "./OnboardingShopInfoContent";

import React, { useState } from "react";

import {
  useCurrentWorkspace,
  useCurrentWorkspaceId,
} from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLocalStorageState } from "@editor/hooks/useLocalStorage";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { projectRouter, projectUtils } from "@editor/trpc/project";
import { userUtils } from "@editor/trpc/user";
import { workspaceUtils } from "@editor/trpc/workspace";

import { useNavigate, useSearchParams } from "react-router-dom";
import { isNullish } from "replo-utils/lib/misc";
import { z } from "zod";

import OnboardingFooter from "./OnboardingFooter";
import OnboardingShopInfoContent from "./OnboardingShopInfoContent";

const OnboardingShopInfo: React.FC = () => {
  const logEvent = useLogAnalytics();
  const { user } = useCurrentUser();
  const workspaceId = useCurrentWorkspaceId();
  const workspace = useCurrentWorkspace();
  const [shopName, setShopName] = useLocalStorageState(
    "onboarding-shopName",
    "",
    { schema: z.string() },
  );
  const [subdomain, setSubdomain] = useLocalStorageState(
    "onboarding-subdomain",
    "",
    { schema: z.string() },
  );
  const [productDescription, setProductDescription] = useLocalStorageState(
    "onboarding-productDescription",
    null,
    {
      schema: z
        .enum([
          "shopify",
          "ecommerce",
          "software",
          "digital",
          "membership",
          "courses",
          "professional",
          "events",
          "retail",
        ])
        .nullable(),
    },
  ) as [ProductDescription | null, (value: ProductDescription | null) => void];
  const [annualRevenue, setAnnualRevenue] = useLocalStorageState(
    "onboarding-annualRevenue",
    null,
    {
      schema: z
        .enum(["0-500k", "500k-2m", "2m-5m", "5m-10m", "10m-50m", "50m+"])
        .nullable(),
    },
  ) as [AnnualRevenue | null, (value: AnnualRevenue | null) => void];

  const [isFormValid, setIsFormValid] = useState(false);

  const isBusinessContext = user?.profileType === "business";
  const isShopifyPaymentProcessor =
    workspace?.preferredPaymentProcessor === "shopify";

  const navigate = useNavigate();

  const { mutateAsync: createProjectAsync, isPending: isCreatingProject } =
    projectRouter.create.useMutation();

  const [searchParams] = useSearchParams();
  const pendingAppInstallationId = searchParams.get("pendingAppInstallationId");
  const shopifyIntegrationId = searchParams.get("shopifyIntegrationId");

  const mapRevenueToNumber = (revenue: string | null): number => {
    switch (revenue) {
      case "0-500k":
        return 0;
      case "500k-2m":
        return 500000;
      case "2m-5m":
        return 2000000;
      case "5m-10m":
        return 5000000;
      case "10m-50m":
        return 10000000;
      case "50m+":
        return 50000000;
      default:
        return 0;
    }
  };

  const handleSubmit = async () => {
    const isCompleteFormValid =
      !isNullish(annualRevenue) &&
      (isShopifyPaymentProcessor || productDescription !== null) &&
      shopName !== "" &&
      (isShopifyPaymentProcessor || subdomain !== "") &&
      isFormValid &&
      user &&
      workspaceId;

    if (!isCompleteFormValid) {
      return;
    }

    if (productDescription === "shopify" && !isShopifyPaymentProcessor) {
      logEvent("onboarding.flow.step.completed", {
        userId: user.email,
        stepId: "business-details",
        stepType: "business-details",
        stepData: {
          shopName,
          subdomain,
          productDescription,
          annualRevenue,
        },
      });

      navigate("/onboarding/setup-shop");
      return;
    }

    const result = await createProjectAsync({
      name: shopName.trim(),
      workspaceId: workspaceId,
      hostingType: isShopifyPaymentProcessor ? "shopify" : "replo",
      shopDomainPrefix: isShopifyPaymentProcessor ? null : subdomain.trim(),
      productDescription: isShopifyPaymentProcessor
        ? "shopify"
        : productDescription,
      annualRevenue: mapRevenueToNumber(annualRevenue),
      workspaceName: `${shopName}'s First Workspace`,
      domain: user.email.split("@")[1]!.toLowerCase(),
    });

    await workspaceUtils.getUserWorkspacesList.invalidate();
    await projectUtils.listWithStats.invalidate();
    await userUtils.get.invalidate();

    logEvent("onboarding.flow.step.completed", {
      userId: user.email,
      stepId: "business-details",
      stepType: "business-details",
      stepData: {
        shopName,
        subdomain,
        productDescription,
        annualRevenue,
      },
    });
    localStorage.removeItem("onboarding-shopName");
    localStorage.removeItem("onboarding-subdomain");
    localStorage.removeItem("onboarding-productDescription");
    localStorage.removeItem("onboarding-annualRevenue");

    // For Shopify users, let the useConnectShopifyIntegrationONavigation hook handle navigation
    // after connecting the integration. For non-Shopify users, navigate immediately.
    if (!isShopifyPaymentProcessor) {
      navigate(`/shops/${result.project?.id}/home`);
    }
    if (!pendingAppInstallationId && !shopifyIntegrationId) {
      navigate(`/shops/${result.project?.id}/home`);
    }
    // Note: Shopify users with pendingAppInstallationId or shopifyIntegrationId will be navigated
    // by the useConnectShopifyIntegrationONavigation hook once their user object comes back with
    // the new project id
  };

  const handleBack = () => {
    navigate("/onboarding/user-info");
  };

  return (
    <div className="h-screen w-full flex flex-col">
      <div className="flex-1 overflow-y-auto flex flex-col items-center md:py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="pt-14 md:pt-0 my-6 flex flex-col gap-1 text-center md:mb-12 md:gap-3">
          <h1 className="header-h1 text-3xl md:text-4xl">
            {isBusinessContext
              ? "Set Up Your Business"
              : "Fill Us In on Your Client"}
          </h1>
          <p className="text-muted header-h1 text-lg font-normal">
            We'll use this info to tailor recommendations for you.
          </p>
        </div>
        <div className="lg:w-[1000px] w-full pb-25 md:pb-40 px-5 lg:px-0">
          <OnboardingShopInfoContent
            shopName={shopName}
            setShopName={setShopName}
            subdomain={subdomain}
            setSubdomain={setSubdomain}
            productDescription={productDescription}
            setProductDescription={(value) => setProductDescription(value)}
            annualRevenue={annualRevenue}
            setAnnualRevenue={(value) => setAnnualRevenue(value)}
            isBusinessContext={isBusinessContext}
            isShopifyPaymentProcessor={isShopifyPaymentProcessor}
            onFormValidityChange={setIsFormValid}
          />
        </div>
      </div>

      <div className="absolute right-0 bottom-0 left-0 z-20">
        <OnboardingFooter
          currentStep={2}
          onNext={handleSubmit}
          onBack={handleBack}
          className="w-full lg:max-w-[1000px] px-5 lg:px-0"
          isNextLoading={isCreatingProject}
          isNextDisabled={isCreatingProject || !isFormValid || !workspaceId}
          totalSteps={2}
        />
      </div>
    </div>
  );
};

export default OnboardingShopInfo;
